import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml, gsapSetLine, pickMediaSrc, isVideoMedia, brandName,
} from "../../reel/sceneShared.js";

export const phoneMockup: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Allyver"]).slice(0, 5);
  const media = pickMediaSrc(inputs.media);
  const isVideo = isVideoMedia(Array.isArray(inputs.media) ? inputs.media[0] : inputs.media);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const phoneW = ctx.format === "9:16" ? 460 : ctx.format === "1:1" ? 420 : 380;
  // Bezel/Padding um den Bildschirm — 16px ringsherum für ein modernes,
  // schmales iPhone-Frame. Der INNERE Screen wird dann auf iPhone-14-Plus
  // Seitenverhältnis (1290:2796 ≈ 1:2.167) ausgelegt, damit echte iPhone-
  // Screenshots PIXELGENAU in den Screen passen — kein Beschnitt, keine
  // schwarzen Balken (cover === contain bei identischem Ratio).
  const bezel = 16;
  const innerW = phoneW - bezel * 2;
  const innerH = Math.round(innerW * (2796 / 1290));
  const phoneH = innerH + bezel * 2;
  const isHoriz = ctx.format !== "9:16";

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const phoneId = `${clipPrefix}-phone`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const screenContent = media
    ? isVideo
      ? `<video src="${escapeHtml(media)}" muted playsinline autoplay loop style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;display:block;"></video>`
      : `<img src="${escapeHtml(media)}" alt="screen" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;display:block;" />`
    : `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};color:${themeColors.fg};font-size:${sm}px;margin-top:40px;padding:24px;">${escapeHtml(brandName)}</div>
${items
  .map(
    (line, i) =>
      `<div id="${clipPrefix}-line-${i}" style="background:${withAlpha(themeColors.fg, 0.08)};border:1px solid ${withAlpha(themeColors.fg, 0.15)};border-radius:24px;padding:16px 24px;color:${themeColors.fg};font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.9}px;margin:0 24px 12px;">${escapeHtml(line)}</div>`,
  )
  .join("")}
<div style="flex:1;"></div>
<div id="${clipPrefix}-cta" style="background:${accent};border-radius:9999px;padding:16px 24px;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;text-align:center;margin:24px;">Jetzt entdecken</div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;padding:64px;display:flex;flex-direction:${isHoriz ? "row" : "column"};align-items:center;justify-content:center;gap:64px;">
  ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.85}px;letter-spacing:-2px;color:${themeColors.fg};text-align:${isHoriz ? "left" : "center"};max-width:${ctx.format === "9:16" ? 760 : 520}px;${isHoriz ? "flex:1;" : ""}">${escapeHtml(headline)}</div>` : ""}
  <div id="${phoneId}" style="position:relative;width:${phoneW}px;height:${phoneH}px;background:linear-gradient(180deg,#1a1a1c 0%,#0a0a0b 100%);border-radius:${Math.round(phoneW * 0.13)}px;padding:${bezel}px;box-shadow:0 0 90px ${withAlpha(accent, 0.35)},0 50px 110px rgba(0,0,0,0.55),inset 0 0 0 1.5px ${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};box-sizing:border-box;">
    <div style="position:relative;width:100%;height:100%;border-radius:${Math.round((phoneW - bezel * 2) * 0.11)}px;background:${media ? "#000" : `linear-gradient(180deg, ${themeColors.bg} 0%, ${withAlpha(accent, 0.25)} 100%)`};overflow:hidden;box-sizing:border-box;${media ? "" : "display:flex;flex-direction:column;gap:16px;"}">
      ${screenContent}
      ${media ? "" : `<div style="position:absolute;top:12px;left:50%;transform:translateX(-50%);width:${Math.round(innerW * 0.34)}px;height:${Math.round(innerW * 0.085)}px;background:#000;border-radius:9999px;z-index:5;box-shadow:0 0 0 1px rgba(255,255,255,0.04);"></div>`}
    </div>
  </div>
</div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${phoneId}`, { opacity: 0, y: 80, rotation: -3 }),
    ...(media ? [] : items.map((_, i) => gsapSetLine(`#${clipPrefix}-line-${i}`, { opacity: 0, y: 12 }))),
    media ? "" : gsapSetLine(`#${clipPrefix}-cta`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${phoneId}`, { opacity: 1, y: 0, rotation: -3, duration: 1.0, ease: "raw:\"power2.out\"" }, 0),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.53, ease: "raw:\"power1.out\"" }, 12 / 30));
  if (!media) {
    items.forEach((_, i) => tweens.push(t.to(`#${clipPrefix}-line-${i}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, (18 + i * 6) / 30)));
    tweens.push(t.to(`#${clipPrefix}-cta`, { opacity: 1, duration: 0.4, ease: "raw:\"power1.out\"" }, (18 + items.length * 6 + 6) / 30));
  }
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
